import React, { useEffect, useState, useCallback } from "react";
import { Bell, X, CheckCircle, AlertTriangle, Info, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export interface SmartNotification {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  persistent?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface SmartNotificationsProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxNotifications?: number;
}

export const SmartNotifications = React.memo(({ 
  position = 'top-right',
  maxNotifications = 5 
}: SmartNotificationsProps) => {
  const [notifications, setNotifications] = useState<SmartNotification[]>([]);
  const { user } = useAuth();

  // إضافة إشعار جديد
  const addNotification = useCallback((notification: Omit<SmartNotification, 'id' | 'timestamp'>) => {
    const newNotification: SmartNotification = {
      ...notification,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date()
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev.slice(0, maxNotifications - 1)];
      return updated;
    });

    // إشعار صوتي
    if (!notification.read) {
      toast(notification.title, {
        description: notification.message,
        duration: notification.persistent ? Infinity : 4000,
      });
    }

    // إزالة تلقائية للإشعارات غير المستمرة
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, 5000);
    }
  }, [maxNotifications]);

  // حذف إشعار
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // تحديد الإشعار كمقروء
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  // مسح جميع الإشعارات
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // الاستماع للتغييرات في قاعدة البيانات
  useEffect(() => {
    if (!user) return;

    const channels = [
      // مراقبة العملاء
      {
        name: 'clients_changes',
        table: 'clients',
        getNotification: (payload: any) => {
          switch (payload.eventType) {
            case 'INSERT':
              return {
                type: 'success' as const,
                title: 'عميل جديد',
                message: `تم إضافة عميل جديد: ${payload.new.name_ar}`,
                read: false
              };
            case 'UPDATE':
              return {
                type: 'info' as const,
                title: 'تحديث عميل',
                message: `تم تحديث بيانات العميل: ${payload.new.name_ar}`,
                read: false
              };
            case 'DELETE':
              return {
                type: 'warning' as const,
                title: 'حذف عميل',
                message: `تم حذف عميل من النظام`,
                read: false
              };
          }
        }
      },
      // مراقبة المهام
      {
        name: 'tasks_changes',
        table: 'tasks',
        getNotification: (payload: any) => {
          switch (payload.eventType) {
            case 'INSERT':
              return {
                type: 'info' as const,
                title: 'مهمة جديدة',
                message: `تم إنشاء مهمة جديدة: ${payload.new.title}`,
                read: false,
                persistent: payload.new.priority === 'urgent'
              };
            case 'UPDATE':
              if (payload.new.status === 'completed') {
                return {
                  type: 'success' as const,
                  title: 'مهمة مكتملة',
                  message: `تم إكمال المهمة: ${payload.new.title}`,
                  read: false
                };
              }
              break;
          }
        }
      },
      // مراقبة الحجوزات
      {
        name: 'bookings_changes',
        table: 'package_bookings',
        getNotification: (payload: any) => {
          switch (payload.eventType) {
            case 'INSERT':
              return {
                type: 'success' as const,
                title: 'حجز جديد',
                message: `تم إنشاء حجز جديد بقيمة ${payload.new.price} ${payload.new.currency}`,
                read: false,
                persistent: true
              };
            case 'UPDATE':
              if (payload.new.status === 'confirmed') {
                return {
                  type: 'success' as const,
                  title: 'تأكيد حجز',
                  message: `تم تأكيد الحجز رقم ${payload.new.package_number}`,
                  read: false
                };
              }
              break;
          }
        }
      }
    ];

    const subscriptions = channels.map(channel => {
      return supabase
        .channel(channel.name)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: channel.table
          },
          (payload) => {
            const notification = channel.getNotification(payload);
            if (notification) {
              addNotification(notification);
            }
          }
        )
        .subscribe();
    });

    return () => {
      subscriptions.forEach(subscription => {
        supabase.removeChannel(subscription);
      });
    };
  }, [user, addNotification]);

  // إشعارات تلقائية للمهام المتأخرة
  useEffect(() => {
    const checkOverdueTasks = async () => {
      if (!user) return;

      try {
        const { data: overdueTasks } = await supabase
          .from('tasks')
          .select('*')
          .lt('due_date', new Date().toISOString())
          .in('status', ['pending', 'in_progress'])
          .limit(5);

        if (overdueTasks && overdueTasks.length > 0) {
          addNotification({
            type: 'warning',
            title: 'مهام متأخرة',
            message: `لديك ${overdueTasks.length} مهام متأخرة تحتاج للمتابعة`,
            read: false,
            persistent: true,
            action: {
              label: 'عرض المهام',
              onClick: () => window.location.href = '/tasks'
            }
          });
        }
      } catch (error) {
        console.error('Error checking overdue tasks:', error);
      }
    };

    // فحص المهام المتأخرة كل ساعة
    const interval = setInterval(checkOverdueTasks, 60 * 60 * 1000);
    checkOverdueTasks(); // فحص فوري

    return () => clearInterval(interval);
  }, [user, addNotification]);

  // أيقونات الإشعارات
  const getNotificationIcon = (type: SmartNotification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-warning" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-info" />;
    }
  };

  // تحديد موقع الإشعارات
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
      default:
        return 'top-4 right-4';
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div 
      className={cn(
        "fixed z-50 w-80 max-w-[calc(100vw-2rem)] space-y-2",
        getPositionClasses()
      )}
    >
      {notifications.map((notification, index) => (
        <Card 
          key={notification.id}
          className={cn(
            "animate-slide-in-right shadow-lg border-l-4 transition-all duration-300",
            notification.type === 'success' && "border-l-success bg-success-light/10",
            notification.type === 'warning' && "border-l-warning bg-warning-light/10",
            notification.type === 'error' && "border-l-destructive bg-destructive-light/10",
            notification.type === 'info' && "border-l-info bg-info-light/10",
            !notification.read && "ring-2 ring-primary/20",
            index > 0 && "scale-95 opacity-90"
          )}
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getNotificationIcon(notification.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-card-foreground text-sm">
                      {notification.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-muted-foreground">
                        {notification.timestamp.toLocaleTimeString('ar-SA', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      
                      {notification.action && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            notification.action!.onClick();
                            markAsRead(notification.id);
                          }}
                          className="h-7 px-3 text-xs"
                        >
                          {notification.action.label}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNotification(notification.id)}
                    className="h-6 w-6 p-0 hover:bg-muted-foreground/10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {notifications.length > 1 && (
        <div className="flex justify-end mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={clearAll}
            className="text-xs h-7 px-3"
          >
            مسح الكل
          </Button>
        </div>
      )}
    </div>
  );
});

SmartNotifications.displayName = "SmartNotifications";